import { getDateFormatted } from '@vindr/utils/helpers/common.helper';
import useRoleMemo from '@vindr/utils/hooks/useRoleMemo';
import useToggle from '@vindr/utils/hooks/useToggle';
import { Form, List } from 'antd';
import { DatePicker } from 'antd-mobile';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormItemProps } from '../FormProps';
import './FormDatePickerMobile.css';

interface FormDatePickerProps extends FormItemProps {
  showTime?: boolean;
  notAccept?: 'past' | 'future';
}

interface DatePickerMobileProps {
  value?: Dayjs;
  onChange?: (date: Dayjs) => void;
  showTime?: boolean;
}

const DatePickerMobile = ({ value, onChange, showTime }: DatePickerMobileProps) => {
  const { t, i18n } = useTranslation('Vindoc');
  const { setFalse: setFalseDate, setTrue: setTrueDate, value: dateFrom } = useToggle();

  const selectedDate = value ? dayjs(value) : dayjs();

  useEffect(() => {
    if (!value) {
      onChange?.(selectedDate);
    }
  }, [value, onChange, selectedDate]);

  const handleConfirm = (val: Date) => {
    const newDate = dayjs(val);
    onChange?.(newDate);
    setFalseDate();
  };

  const isVietnamese = i18n.language === 'vi';

  return (
    <>
      <List.Item
        onClick={() => {
          setTrueDate();
        }}
        className="flex justify-between border-[1px] border-[#9adafb63] p-3"
      >
        {selectedDate.format(getDateFormatted(showTime))}
      </List.Item>
      <DatePicker
        visible={dateFrom}
        onClose={setFalseDate}
        onConfirm={handleConfirm}
        precision={showTime ? 'second' : 'day'}
        defaultValue={selectedDate.toDate()}
        confirmText={t('Confirm')}
        cancelText={t('Cancel')}
        className={`ant-picker-mobile ${isVietnamese ? 'vi-style' : 'en-style'}`}
      />
    </>
  );
};

const FormDatePickerMobile = ({
  name,
  label,
  // placeholder,
  rules,
  required,
  disabled,
  showTime,
  // notAccept = 'past',
  className,
  noStyle,
}: // ...props
FormDatePickerProps): JSX.Element => {
  const { t } = useTranslation('Vindoc');
  const rulesMemo = useRoleMemo({ required, rules, disabled });
  const today = dayjs();

  return (
    <Form.Item
      name={name}
      label={t(label)}
      rules={rulesMemo}
      initialValue={showTime ? today : today.startOf('day')}
      getValueProps={(value) => ({
        value: value ? (showTime ? dayjs(value) : dayjs(value).startOf('day')) : null,
      })}
      getValueFromEvent={(value) =>
        value ? (showTime ? value.toISOString() : value.startOf('day').toISOString()) : null
      }
      className={className}
      noStyle={noStyle}
    >
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue, setFieldsValue }) => {
          const value = getFieldValue(name);
          const handleChange = (date: Dayjs) => {
            setFieldsValue({ [name]: date });
          };
          return <DatePickerMobile value={value} onChange={handleChange} showTime={showTime} />;
        }}
      </Form.Item>
    </Form.Item>
  );
};

export default FormDatePickerMobile;
